import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Divider,
  Stack,
  Button,
  Chip,
} from "@mui/material";
import {
  CalendarToday,
  CheckCircle,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";

export interface PastureData {
  _key: string;
  _id: string;
  _rev: string;
  farm_id: string;
  name: string;
  category?: string;
  area_hectares?: number | null;
  capacity_head?: number | null;
  current_occupancy?: number | null;
  purpose?: string;
  grass_type?: string;
  implantation_date?: string | null;
  description?: string;
  soil_condition?: string | null;
  water_source?: string;
  fencing_condition?: string | null;
  last_fertilized?: string | null;
  fertilizer_type?: string;
  is_active: boolean;
  status?: string;
  created_at?: string;
  updated_at?: string;
  gps_coordinates?: any;
  rotation_schedule?: any;
}

export interface PastureListData {
  data: PastureData[];
  resumo?: string;
  url?: string;
}

export interface PastureListViewProps {
  pastureListData: PastureListData;
}

const formatDate = (dateString: string | undefined | null, t: any): string => {
  if (!dateString) return t('common:labels.date_not_available');
  try {
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    return new Date(dateString as string).toLocaleDateString();
  } catch {
    return String(dateString);
  }
};

export const PastureListView: React.FC<PastureListViewProps> = ({ pastureListData }) => {
  const { t } = useTranslation(['farm', 'common']);
  const { data: pastures } = pastureListData;

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const totalPages = Math.ceil((pastures?.length || 0) / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = pastures.slice(startIndex, startIndex + itemsPerPage);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const totalArea = pastures.reduce((sum, p) => sum + (p.area_hectares || 0), 0);
  const activeCount = pastures.filter(p => p.is_active).length;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      {/* Summary Card */}
      <Card sx={{ mb: 3, bgcolor: DESIGN_TOKENS.colors.background.elevated }}>
        <CardContent>
          <Stack direction="row" spacing={4} alignItems="center">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar sx={{ bgcolor: DESIGN_TOKENS.colors.organization.farm, width: 40, height: 40 }}>
                <img src="/gestores/Pastos.svg" alt="Pastos" style={{ width: '24px', height: '24px', filter: 'brightness(0) invert(1)' }} />
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('labels.total_pastures', { ns: 'farm' })}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {pastures.length}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckCircle sx={{ color: DESIGN_TOKENS.colors.status.success }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('labels.active_pastures', { ns: 'farm' })}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {activeCount}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarToday sx={{ fontSize: 20, color: DESIGN_TOKENS.colors.text.secondary }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('labels.total_area_hectares', { ns: 'farm' })}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {totalArea > 0 ? `${totalArea.toFixed(2)} ha` : t('common:not_informed')}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Pastures Grid */}
      {currentItems.length > 0 ? (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {currentItems.map((pasto) => (
              <Box sx={{ width: { xs: '100%', md: 'calc(50% - 12px)', lg: 'calc(33.333% - 16px)' } }} key={pasto._key}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.2s ease-in-out', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Avatar sx={{ bgcolor: DESIGN_TOKENS.colors.organization.farm, width: 40, height: 40 }}>
                        <img src="/gestores/Pastos.svg" alt="Pastos" style={{ width: '24px', height: '24px', filter: 'brightness(0) invert(1)' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.text.primary }}>
                          {pasto.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                          ID: {pasto._key}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Descrição */}
                    {pasto.description && (
                      <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 2, fontStyle: 'italic' }}>
                        {pasto.description}
                      </Typography>
                    )}

                    {/* Informações do Pasto */}
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={pasto.category || t('labels.pasture', { ns: 'farm' })} size="small" variant="outlined" />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {t('labels.purpose', { ns: 'farm' })}: <strong>{pasto.purpose || t('common:not_informed')}</strong>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {t('labels.area_hectares', { ns: 'farm' })}: <strong>{pasto.area_hectares != null ? `${pasto.area_hectares} ha` : t('common:not_informed')}</strong>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {t('labels.capacity_head', { ns: 'farm' })}: <strong>{pasto.capacity_head != null ? pasto.capacity_head : t('common:not_informed')}</strong>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {t('labels.current_occupancy', { ns: 'farm' })}: <strong>{pasto.current_occupancy != null ? pasto.current_occupancy : 0}</strong>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary }} />
                        <Typography variant="body2">
                          {t('labels.created_at', { ns: 'farm' })} {formatDate(pasto.created_at, t)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary }} />
                        <Typography variant="body2">
                          {t('labels.updated_at', { ns: 'farm' })} {formatDate(pasto.updated_at, t)}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Fertilização / Grama */}
                    {(pasto.grass_type || pasto.last_fertilized || pasto.fertilizer_type) && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: DESIGN_TOKENS.colors.text.primary }}>
                            {t('labels.soil_and_grass', { ns: 'farm' })}
                          </Typography>
                          <Stack spacing={0.5}>
                            {pasto.grass_type && (
                              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                                {t('labels.grass_type', { ns: 'farm' })}: {pasto.grass_type}
                              </Typography>
                            )}
                            {pasto.last_fertilized && (
                              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                                {t('labels.last_fertilized', { ns: 'farm' })}: {formatDate(pasto.last_fertilized, t)}
                              </Typography>
                            )}
                            {pasto.fertilizer_type && (
                              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                                {t('labels.fertilizer_type', { ns: 'farm' })}: {pasto.fertilizer_type}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 2 }}>
              <Button variant="outlined" startIcon={<NavigateBefore />} onClick={handlePreviousPage} disabled={currentPage === 1}>
                {t('buttons.previous', { ns: 'common' })}
              </Button>
              <Typography variant="body2" sx={{ mx: 2 }}>
                {t('labels.page_of', { ns: 'common', current: currentPage, total: totalPages })}
              </Typography>
              <Button variant="outlined" endIcon={<NavigateNext />} onClick={handleNextPage} disabled={currentPage === totalPages}>
                {t('buttons.next', { ns: 'common' })}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <img src="/gestores/Pastos.svg" alt="Pastos" style={{ width: '64px', height: '64px', opacity: 0.3, marginBottom: '16px' }} />
            <Typography variant="h6" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1 }}>
              {t('empty_states.no_pastures', { ns: 'farm' })}
            </Typography>
            <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              {t('empty_states.no_pastures_description', { ns: 'farm' })}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PastureListView;